/* eslint-disable @typescript-eslint/no-explicit-any */
import Accordian from "@/components/atoms/accordion";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { RichTextField } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";

type QnAItem = {
  question: string;
  answer: RichTextField;
};

type Tab = {
  id: string;
  question: string;
  answers: RichTextField | QnAItem[];
};

type TabsProductProps<T extends Tab[]> = {
  tabs: T;
  defaultTabId?: T[number]["id"];
  className?: string;
  accordionClassname?: string;
};

function isQnaArray(answers: RichTextField | QnAItem[]): answers is QnAItem[] {
  return (
    Array.isArray(answers) &&
    answers.length > 0 &&
    typeof answers[0] === "object" &&
    answers[0] !== null &&
    "question" in answers[0] &&
    "answer" in answers[0]
  );
}

function isRichTextField(answers: any): answers is RichTextField {
  return (
    Array.isArray(answers) &&
    answers.length > 0 &&
    answers.some((block) => {
      return (
        typeof block === "object" &&
        block !== null &&
        "type" in block &&
        typeof block.type === "string" &&
        "text" in block &&
        typeof block.text === "string" &&
        block.text.trim() !== ""
      );
    })
  );
}

const TabsProduct = <T extends Tab[]>({ tabs, defaultTabId, className, accordionClassname }: TabsProductProps<T>) => {
  const filteredTabs = tabs.filter((tab) => {
    const { answers } = tab;

    if (isQnaArray(answers)) {
      return answers.length > 0 && answers.some((item) => item.answer?.length > 0);
    }

    return isRichTextField(answers);
  });

  const defaultTab = defaultTabId || (filteredTabs.length > 0 ? filteredTabs[0].id : "");

  const qnas = filteredTabs.map((tab) => ({
    question: tab.question,
    answers: isQnaArray(tab.answers) ? tab.answers : [{ question: "", answer: tab.answers }],
  }));

  if (filteredTabs.length === 0) return null;

  return (
    <section className={cn("container", className)}>
      <Tabs defaultValue={defaultTab} className="hidden gap-5 sm:flex">
        <ScrollArea className="w-full whitespace-nowrap">
          <TabsList className="bg-white-primary font-cabinet-grotesk w-full justify-start gap-12 rounded-none border-b border-[#E5E7EB] p-0 px-7.5 py-2.5">
            {filteredTabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="data-[state=active]:border-b-blue-brand text-dark-primary flex-0 cursor-pointer justify-start rounded-none border-2 border-b-2 p-0 text-2xl ease-in-out data-[state=active]:-mb-4 data-[state=active]:pb-4 data-[state=active]:font-bold data-[state=active]:shadow-none"
              >
                {tab.question}
              </TabsTrigger>
            ))}
          </TabsList>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {filteredTabs.map((tab) => (
          <TabsContent
            key={tab.id}
            value={tab.id}
            className={cn("px-7.5", tab.id === "questions" ? "text-dark-primary" : "text-primary-light")}
          >
            {isQnaArray(tab.answers) ? (
              <div className="space-y-2.5">
                {tab.answers.map((item, index) => (
                  <div key={index} className={cn("space-y-2.5 pb-2.5")}>
                    {index !== 0 && <hr className="h-px w-full bg-gray-200" />}
                    <h6 className="font-medium">{item.question}</h6>
                    <div className="text-sm">
                      <PrismicRichText field={item.answer} />
                    </div>
                  </div>
                ))}
              </div>
            ) : isRichTextField(tab.answers) ? (
              <div className="prose max-w-none">
                <PrismicRichText field={tab.answers} />
              </div>
            ) : (
              <div>No content</div>
            )}
          </TabsContent>
        ))}
      </Tabs>
      <Accordian className={cn("max-w-full sm:hidden", accordionClassname)} qnas={qnas} />
    </section>
  );
};

export default TabsProduct;
